import { Component, computed, inject, input, linkedSignal, signal } from '@angular/core';
import { rxResourceFixed, LookupsService } from '@zayan-erp/shared';
import { map, of } from 'rxjs';
import {
  ApsDealDocument,
  DealDocumentCustomTemplateState,
  DealDocumentTemplateState,
  DocumentTemplateWithSelected
} from '../../../models/deal-document.models';
import { SalesApsService } from '../../../services/sales-aps.service';
import { BuyerContract } from '../../../models/buyer-contract.model';
import { DocGeneration } from './doc-generation/doc-generation';
import { SignableDocumentCard } from './signable-document-card/signable-document-card';
import { DealDocumentsService } from '@zayan-erp/sales';
import { UploadExternalContract } from '../miscellaneous-docs/upload-external-contract/upload-external-contract';
import { ExternalSigning } from '../miscellaneous-docs/external-signing/external-signing';

@Component({
  selector: 'lib-deal-documents',
  imports: [SignableDocumentCard, DocGeneration, UploadExternalContract, ExternalSigning],
  templateUrl: './deal-documents.component.html',
  styleUrl: './deal-documents.component.scss'
})
export class DealDocumentsComponent {
  private readonly EMPTY_TEMPLATE_STATE: DealDocumentTemplateState = {
    orderedTemplateIds: [],
    customTemplates: []
  };

  readonly #salesApsService = inject(SalesApsService);
  readonly #lookupsService = inject(LookupsService);
  readonly #dealDocService = inject(DealDocumentsService);

  isActive = input<boolean>(true);
  projectCode = input<string>('');

  effectiveDeal = computed(() => this.#salesApsService.currentDeal());
  effectiveDealId = computed(() => this.effectiveDeal()?.id ?? '');
  effectiveProjectCode = computed(() => this.effectiveDeal()?.project.projectCode ?? '');

  contractDetailsResource = rxResourceFixed<BuyerContract, { dealId: string }>({
    params: () => ({ dealId: this.effectiveDealId() ?? '' }),
    stream: ({ params }) => {
      if (!params.dealId) {
        return of({} as BuyerContract);
      }
      return this.#salesApsService
        .getBuyerContract(params.dealId)
        .pipe(map(contract => contract ?? ({} as BuyerContract)));
    },
    defaultValue: {} as BuyerContract
  });

  contractDetails = computed(() => this.contractDetailsResource.value());

  getProjectId = rxResourceFixed({
    params: () => this.effectiveProjectCode() ?? '',
    defaultValue: '',
    stream: ({ params }) => {
      if (!params) return of('');
      return this.#lookupsService
        .readProjects()
        .pipe(map(projects => projects.find(project => project.projCode === params)?.id ?? ''));
    }
  });

  projectId = computed(() => this.getProjectId.value() ?? '');

  documentsResource = rxResourceFixed<ApsDealDocument[], string>({
    params: () => this.effectiveDealId() ?? '',
    defaultValue: [],
    stream: ({ params }) => {
      if (!params) {
        return of([]);
      }
      return this.#salesApsService.getDealDocuments(params);
    }
  });

  documents = computed(() => {
    const raw = this.documentsResource.value();
    return Array.isArray(raw) ? raw : [];
  });

  private readonly shouldLoadDocumentTemplates = computed(() => {
    const documentsResolved =
      this.documentsResource.status() === 'resolved' || this.documentsResource.status() === 'local';
    return documentsResolved && this.documents().length === 0;
  });

  documentTemplatesResource = rxResourceFixed<
    DocumentTemplateWithSelected[],
    { projectId: string; dealId: string; loadTemplates: boolean }
  >({
    params: () => ({
      projectId: this.projectId(),
      dealId: this.effectiveDealId(),
      loadTemplates: this.shouldLoadDocumentTemplates()
    }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.loadTemplates || !params.projectId || !params.dealId) {
        return of([]);
      }
      return this.#salesApsService.getDocumentTemplatesByProject(params.projectId).pipe(
        map(templates =>
          templates.map(template => ({
            ...template,
            isSelected: false,
            isCustom: false,
            isPositionLocked: Boolean(template.isPositionLocked)
          }))
        ),
        map(templates => this.applyPersistedCustomTemplates(templates))
      );
    }
  });

  documentTemplates = linkedSignal(() => this.documentTemplatesResource.value() ?? []);

  reloadDocuments(): void {
    this.documentsResource.reload();
    this.contractDetailsResource.reload();
  }

  private applyPersistedCustomTemplates(
    systemTemplates: DocumentTemplateWithSelected[]
  ): DocumentTemplateWithSelected[] {
    const dealId = this.effectiveDealId();
    if (!dealId) {
      return systemTemplates;
    }

    const persistedTemplateState = this.getTransientTemplateState(dealId);
    if (!persistedTemplateState.customTemplates.length && !persistedTemplateState.orderedTemplateIds.length) {
      return systemTemplates;
    }

    const customTemplates = persistedTemplateState.customTemplates.map(persistedTemplate =>
      this.buildCustomTemplate(persistedTemplate)
    );
    const mergedTemplates = [...systemTemplates, ...customTemplates];
    const orderedTemplates = this.applyTemplateStateOrder(mergedTemplates, persistedTemplateState);
    return orderedTemplates.map((template, index) => ({ ...template, sequence: index + 1 }));
  }

  private buildCustomTemplate(persistedTemplate: DealDocumentCustomTemplateState): DocumentTemplateWithSelected {
    return {
      id: persistedTemplate.customDocumentId,
      projectId: this.projectId(),
      name: persistedTemplate.customDocumentName,
      s3Document: {
        id: persistedTemplate.customDocumentId,
        s3Key: '',
        directUrl: ''
      },
      templateVariable: { id: '', name: '' },
      sequence: 0,
      isSelected: false,
      isCustom: true,
      isPositionLocked: false
    };
  }

  private getTransientTemplateState(dealId: string): DealDocumentTemplateState {
    return this.#dealDocService.getDealDocumentTemplateState(dealId) ?? this.EMPTY_TEMPLATE_STATE;
  }

  private applyTemplateStateOrder(
    templates: DocumentTemplateWithSelected[],
    state: DealDocumentTemplateState
  ): DocumentTemplateWithSelected[] {
    const templateById = new Map(templates.map(template => [template.id, template]));
    const orderedTemplatesFromState: DocumentTemplateWithSelected[] = [];
    const consumedTemplateIds = new Set<string>();

    for (const templateId of state.orderedTemplateIds ?? []) {
      const template = templateById.get(templateId);
      if (!template) {
        continue;
      }
      orderedTemplatesFromState.push(template);
      consumedTemplateIds.add(templateId);
    }

    for (const template of templates) {
      if (!consumedTemplateIds.has(template.id)) {
        orderedTemplatesFromState.push(template);
      }
    }

    const orderedSystemTemplates = orderedTemplatesFromState.filter(template => !template.isCustom);
    const systemTemplateIds = new Set(orderedSystemTemplates.map(template => template.id));
    const customTemplatesByAnchor = new Map<string, DocumentTemplateWithSelected[]>();
    const unanchoredCustomTemplates: DocumentTemplateWithSelected[] = [];
    const consumedCustomIds = new Set<string>();

    for (const customState of state.customTemplates) {
      const customTemplate = templateById.get(customState.customDocumentId);
      if (!customTemplate?.isCustom) {
        continue;
      }

      consumedCustomIds.add(customTemplate.id);
      const anchorId = customState.anchorSystemTemplateId;
      if (anchorId && systemTemplateIds.has(anchorId)) {
        const anchoredTemplates = customTemplatesByAnchor.get(anchorId) ?? [];
        anchoredTemplates.push(customTemplate);
        customTemplatesByAnchor.set(anchorId, anchoredTemplates);
      } else {
        unanchoredCustomTemplates.push(customTemplate);
      }
    }

    for (const template of orderedTemplatesFromState) {
      if (template.isCustom && !consumedCustomIds.has(template.id)) {
        unanchoredCustomTemplates.push(template);
      }
    }

    const orderedTemplates: DocumentTemplateWithSelected[] = [];
    for (const systemTemplate of orderedSystemTemplates) {
      orderedTemplates.push(systemTemplate);
      orderedTemplates.push(...(customTemplatesByAnchor.get(systemTemplate.id) ?? []));
    }
    orderedTemplates.push(...unanchoredCustomTemplates);

    return orderedTemplates;
  }

  replaceRequested(): void {
    this.#dealDocService.requestOpenUploadExternalContractModal();
  }
}
